import { UserRoles } from '@securecore-new-application/securecore-datacore/lib/types';
import { Dropdown, Layout, Menu, notification, Popover } from 'antd';
import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';

import logo from '../../assets/images/logo.svg';
import logoCollapsed from '../../assets/images/logo-collapsed.svg';
import { COMPANY_PHONE } from '../../constants';
import { useCurrentUser, useGetScreenStatus } from '../../hooks';
import { NotificationMessages } from '../../notification';
import { SCREEN_STATUS } from '../../types';
import { Button } from '../ui/Button';
import Icon, { IconTypes } from '../ui/Icon/Icon';
import { theme } from '../ui/themes/default';
import { Text } from '../ui/Typography';
import {
  CollapseButton,
  CollapsedLogoWrapper,
  ContentWrapper,
  EmergencySection,
  ExpandedLogoWrapper,
  IconWrapper,
  MenuLabel,
  PhoneLink,
  StyledCopy,
  StyledDivider,
  StyledDropdownItem,
  StyledDropdownItemText,
  StyledDropdownMenu,
  StyledDropdownWrap,
  StyledLink,
  StyledLinkDropdown,
  StyledMenuItem,
} from './Navbar.styles';

const { Sider } = Layout;

interface NavbarConfigItem {
  path: string;
  label: string | JSX.Element;
  icon: IconTypes;
  access?: UserRoles[];
}

interface Props {
  collapsable?: boolean;
  footer?: boolean;
  disableLinks?: boolean;
  config: NavbarConfigItem[];
  emergencyCallClick: () => void;
}

export function Navbar({ collapsable, footer, disableLinks, config, emergencyCallClick }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const currentScreen = useGetScreenStatus();
  const currentUser = useCurrentUser();
  const location = useLocation();

  const isExtraSmallScreen = currentScreen === SCREEN_STATUS.extraSmall;
  const isSmallScreen = currentScreen === SCREEN_STATUS.small;

  const isAdaptive = collapsed || isSmallScreen;

  const handleCollapse = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  const copyNumber = useCallback(() => {
    navigator.clipboard.writeText(COMPANY_PHONE);
    notification.success({ message: NotificationMessages.COPIED_CLIPBOARD });
  }, []);
  const renderEmergencySection = useCallback(() => {
    if (isAdaptive) {
      return (
        <EmergencySection collapsed={isAdaptive}>
          <Popover
            placement="right"
            content={
              <>
                <StyledLinkDropdown href={`tel:${COMPANY_PHONE}`} onClick={emergencyCallClick}>
                  <Icon icon={IconTypes.Phone} />
                  <StyledDropdownItem>
                    <StyledDropdownItemText>
                      <Text variant="description" color="secondary">
                        Emergency call
                      </Text>
                    </StyledDropdownItemText>
                    <PhoneLink href={`tel:${COMPANY_PHONE}`} onClick={emergencyCallClick}>
                      <Text variant="bodyMedium">{COMPANY_PHONE}</Text>
                    </PhoneLink>
                  </StyledDropdownItem>
                </StyledLinkDropdown>
                <StyledDivider />
              </>
            }
          >
            <StyledLinkDropdown>
              <Button variant="primary" style={{ width: 56, height: 56 }}>
                <Icon icon={IconTypes.EmergencyRequest} fillColor={theme.white} />
              </Button>
            </StyledLinkDropdown>
          </Popover>
        </EmergencySection>
      );
    }
    const menu = (
      <StyledDropdownMenu>
        <Menu>
          <Menu.Item>
            <StyledDropdownWrap>
              <Icon icon={IconTypes.Phone} />
              <StyledDropdownItem>
                <StyledDropdownItemText>
                  <Text variant="description" color="secondary">
                    Emergency call
                  </Text>
                </StyledDropdownItemText>
                <PhoneLink href={`tel:${COMPANY_PHONE}`} onClick={emergencyCallClick}>
                  <Text variant="bodyMedium">{COMPANY_PHONE}</Text>
                </PhoneLink>
              </StyledDropdownItem>
              <StyledCopy onClick={copyNumber}>
                <Icon icon={IconTypes.Copy} />
              </StyledCopy>
            </StyledDropdownWrap>
          </Menu.Item>
        </Menu>
      </StyledDropdownMenu>
    );

    return (
      <EmergencySection collapsed={isAdaptive}>
        <Dropdown overlay={menu} placement="top">
          <Button
            variant="emergency"
            buttonSize="large"
            leftIcon={<Icon icon={IconTypes.EmergencyRequest} fillColor={theme.white} />}
            rightIcon={<Icon icon={IconTypes.AngleDown} fillColor={theme.white} />}
            fullWidth
          >
            Request Emergency Service
          </Button>
        </Dropdown>
      </EmergencySection>
    );
  }, [isAdaptive, emergencyCallClick, copyNumber]);

  const renderContent = useCallback(() => {
    return (
      <ContentWrapper>
        <div>
          {!isSmallScreen && collapsable && (
            <CollapseButton onClick={handleCollapse}>
              <Icon
                icon={isAdaptive ? IconTypes.AngleRight : IconTypes.AngleLeft}
                fillColor={theme.collapseButtonColor}
              />
            </CollapseButton>
          )}
          <StyledLink to="/">
            {isAdaptive ? <CollapsedLogoWrapper src={logoCollapsed} /> : <ExpandedLogoWrapper src={logo} />}
          </StyledLink>
          <Menu selectedKeys={[location.pathname]}>
            {config
              .filter((item) => item.access?.find((role) => role === currentUser?.role))
              .map((item) => (
                <StyledMenuItem key={item.path} active={item.path === location.pathname} collapsed={isAdaptive}>
                  {disableLinks ? (
                    <>
                      <IconWrapper>
                        <Icon
                          icon={item.icon}
                          fillColor={item.path === location.pathname ? theme.primary : theme.darkGrey}
                        />
                      </IconWrapper>
                      {!isAdaptive && <MenuLabel>{item.label}</MenuLabel>}
                    </>
                  ) : (
                    <StyledLink to={item.path}>
                      <IconWrapper>
                        <Icon
                          icon={item.icon}
                          fillColor={item.path === location.pathname ? theme.primary : theme.darkGrey}
                        />
                      </IconWrapper>
                      {!isAdaptive && <MenuLabel>{item.label}</MenuLabel>}
                    </StyledLink>
                  )}
                </StyledMenuItem>
              ))}
          </Menu>
        </div>

        {footer && renderEmergencySection()}
      </ContentWrapper>
    );
  }, [
    isSmallScreen,
    collapsable,
    handleCollapse,
    isAdaptive,
    location.pathname,
    config,
    footer,
    renderEmergencySection,
    disableLinks,
    currentUser,
  ]);

  return !isExtraSmallScreen ? (
    <Sider collapsible collapsed={isAdaptive} width={260} collapsedWidth={88} trigger={null}>
      {renderContent()}
    </Sider>
  ) : null;
}
